import { Wordmark } from "../components/Logo.tsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Button, Card, Checkbox, Input, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowUndo16Regular, LockClosed16Regular, Location16Regular, Open16Regular, Send20Filled, Timer16Regular } from "@fluentui/react-icons";
import { WEEKDAYS, ClientSelfSignupInput, GENDER_LABEL, PersonName, type Gender, ageOn, normalisePhone, parseDateOfBirth, applyTimeLimits, parseTimeLimits, type ClientPreferenceInput } from "@gymplan/shared";
import { api, type AvailabilitySlot } from "../lib/api.ts";
import { DayChips, PARTS } from "../components/DayChips.tsx";
import { ChatLog, type ChatMessage } from "../components/ChatLog.tsx";
import { WEEKDAY_LABEL } from "../lib/format.ts";

interface InviteInfo {
  trainerName: string;
  photoUrl: string | null;
  sessionLengthMinutes: number;
  gyms: { id: string; name: string; city: string | null; isPrimary: boolean }[];
  availability: AvailabilitySlot[];
}

const useStyles = makeStyles({
  page: { minHeight: "100dvh", backgroundColor: tokens.colorNeutralBackground2, display: "flex", justifyContent: "center" },
  col: { width: "100%", maxWidth: "560px", padding: "16px", display: "flex", flexDirection: "column", gap: "12px", boxSizing: "border-box" },
  trainer: { display: "flex", flexDirection: "row", alignItems: "center", gap: "14px", padding: "12px 14px" },
  meta: { display: "flex", flexWrap: "wrap", gap: "4px 12px", color: tokens.colorNeutralForeground3 },
  metaItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
  chat: { display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
  composer: { display: "flex", flexDirection: "column", gap: "10px", padding: "12px 16px 16px", borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  chips: { display: "flex", flexWrap: "wrap", gap: "8px" },
  inputRow: { display: "flex", gap: "8px" },
  days: { maxHeight: "44vh", overflowY: "auto", margin: "0 -4px", padding: "0 4px" },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
  secure: { display: "inline-flex", alignItems: "center", gap: "4px", color: tokens.colorNeutralForeground3, alignSelf: "center" },
});

type Step = "intro" | "name" | "dob" | "gender" | "phone" | "email" | "sessions" | "prefs" | "limits" | "consent";
const ORDER: Step[] = ["intro", "name", "dob", "gender", "phone", "email", "sessions", "prefs", "limits", "consent"];
interface Answers {
  intro?: "yes" | "no";
  name?: string;
  /** "YYYY-MM-DD": confirms 18+ (re-checked by the server) and is kept, per the privacy notice. */
  dob?: string;
  gender?: { value: Gender; description?: string };
  phone?: string;
  email?: string | null;
  sessions?: number;
  prefs?: ClientPreferenceInput[];
  /** Extra detail on top of the parts of the day ("not before 8:30am"); null = none. */
  limits?: { text: string; prefs: ClientPreferenceInput[] } | null;
  consent?: { whatsappOptIn: boolean };
}

const first = (n: string) => n.trim().split(/\s+/)[0] ?? n;

/** "Mon morning & evening · Sat any time" */
function prefsSummary(prefs: ClientPreferenceInput[]): string {
  return WEEKDAYS.filter((d) => prefs.some((p) => p.weekday === d))
    .map((d) => {
      const mine = prefs.filter((p) => p.weekday === d);
      const parts = mine.some((p) => !p.startTime) ? ["any time"] : PARTS.filter((q) => mine.some((p) => p.startTime === q.start)).map((q) => q.label.toLowerCase());
      return `${WEEKDAY_LABEL[d].slice(0, 3)} ${parts.join(" & ")}`;
    })
    .join(" · ");
}

/** Exact times, one day per line: "• Mon 08:30–12:00". */
function exactSummary(prefs: ClientPreferenceInput[]): string {
  return WEEKDAYS.filter((d) => prefs.some((p) => p.weekday === d))
    .map((d) => {
      const mine = prefs.filter((p) => p.weekday === d).sort((x, y) => (x.startTime ?? "").localeCompare(y.startTime ?? ""));
      return `• ${WEEKDAY_LABEL[d].slice(0, 3)} ${mine.some((p) => !p.startTime) ? "any time" : mine.map((p) => `${p.startTime}–${p.endTime}`).join(", ")}`;
    })
    .join("\n");
}

/** The chat, rebuilt from the answers so far: what RackUp has said, what the client said, and the next question. */
function script(info: InviteInfo, a: Answers, extra: ChatMessage[]): { messages: ChatMessage[]; step: Step | "declined" | "underage" | null } {
  const t = info.trainerName;
  const tf = first(t);
  const gym = info.gyms.find((g) => g.isPrimary) ?? info.gyms[0];
  const where = gym ? ` at ${gym.name}${gym.city && !gym.name.toLowerCase().includes(gym.city.toLowerCase()) ? `, ${gym.city}` : ""}` : "";
  const m: ChatMessage[] = [
    { role: "bot", text: `Hi 👋 I'm RackUp — I help ${tf} plan training sessions.` },
    { role: "bot", text: `${t} is a personal trainer${where}, looking for ${info.sessionLengthMinutes}-minute sessions. They sent you this link to find out when you can train.` },
    { role: "bot", text: `What you tell me here is kept secure. It's encrypted, it's only used to arrange your sessions with ${tf}, and it's never sold or used for marketing.` },
    { role: "bot", text: `First — is ${tf} the trainer you were expecting?` },
  ];
  const me = (text: string) => m.push({ role: "me", text });
  const bot = (text: string) => m.push({ role: "bot", text });

  if (!a.intro) return { messages: m, step: "intro" };
  if (a.intro === "no") {
    me("No, that's not them");
    bot("No problem — nothing has been saved. Check with whoever sent you the link that it's the right one.");
    return { messages: m, step: "declined" };
  }
  me(`Yes, that's ${tf}`);
  bot("Great. What's your full name?");
  if (!a.name) return { messages: m, step: "name" };
  me(a.name);
  bot(`Nice to meet you, ${first(a.name)}. What's your date of birth? It confirms you're 18 or over and lets ${tf} plan your training around your age.`);
  if (!a.dob) return { messages: m, step: "dob" };
  me(new Date(`${a.dob}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }));
  if (ageOn(a.dob) < 18) {
    bot(`Thanks, ${first(a.name)}. RackUp is only for people aged 18 and over, so I can't sign you up here — and I haven't saved anything.`);
    bot(`Please speak to ${tf} directly about training.`);
    return { messages: m, step: "underage" };
  }
  bot("Thanks. How would you describe your gender? It's optional — RackUp only uses it to get your pronouns right.");
  if (a.gender === undefined) return { messages: m, step: "gender" };
  me(a.gender.value === "self_described" ? (a.gender.description ?? "In my own words") : GENDER_LABEL[a.gender.value]);
  bot("Thanks. What's your WhatsApp number? That's how I'll check in with you each week.");
  if (!a.phone) return { messages: m, step: "phone" };
  me(a.phone);
  bot("Want to add an email address too? It's optional.");
  if (a.email === undefined) return { messages: m, step: "email" };
  me(a.email ?? "Skip");
  bot(`How many sessions a week would you like with ${tf}?`);
  if (!a.sessions) return { messages: m, step: "sessions" };
  me(`${a.sessions} a week`);
  bot(`When can you usually train? Tap the parts of each day that work — ${tf}'s hours are shown, and you can change this any time.`);
  if (!a.prefs) return { messages: m, step: "prefs" };
  me(prefsSummary(a.prefs));
  bot("Got it. Anything more specific I should know? For example “not before 8:30am” or “finish by 7pm on Fridays”.");
  if (a.limits === undefined) return { messages: m, step: "limits" };
  if (a.limits) {
    me(a.limits.text);
    bot(`Thanks — so the times that work for you are:\n${exactSummary(a.limits.prefs)}`);
  } else me("No, that's it");
  bot(`Nearly done, ${first(a.name)}. Before I save anything, please check these:`);
  if (!a.consent) return { messages: m, step: "consent" };
  me("Agreed — sign me up");
  m.push(...extra);
  return { messages: m, step: null };
}

/** Client sign-up as a short chat with RackUp, introduced by the trainer's details so the client knows who it's for. */
export function JoinPage() {
  const s = useStyles();
  const { token } = useParams();
  const info = useQuery({ queryKey: ["invite", token], queryFn: () => api.get<InviteInfo>(`/api/clients/invite/${token}`), retry: false });
  const [a, setA] = useState<Answers>({});
  const [extra, setExtra] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<ClientPreferenceInput[]>([]);
  const [boxes, setBoxes] = useState({ acceptPrivacy: false, whatsappOptIn: true });
  const [busy, setBusy] = useState(false);
  const [portal, setPortal] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);
  const [log, setLog] = useState({ key: 0, from: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const onSettled = useCallback((v: boolean) => setSettled(v), []);

  const sc = useMemo(() => (info.data ? script(info.data, a, extra) : null), [info.data, a, extra]);

  if (info.isLoading)
    return (
      <Page s={s}>
        <Spinner />
      </Page>
    );
  if (info.isError || !info.data || !sc)
    return (
      <Page s={s}>
        <Text size={500} weight="semibold">This link isn't valid</Text>
        <Text>Ask your trainer for a fresh one.</Text>
      </Page>
    );
  const d = info.data;
  const tf = first(d.trainerName);
  const gym = d.gyms.find((g) => g.isPrimary) ?? d.gyms[0];
  const step = sc.step;

  const answer = (patch: Answers) => {
    setErr(null);
    setText("");
    setA((x) => ({ ...x, ...patch }));
  };

  /** Step back one question (the chat redraws without retyping). */
  const undo = () => {
    const done = ORDER.filter((k) => a[k] !== undefined);
    const last = done.at(-1);
    if (!last) return;
    const nextA = { ...a };
    delete nextA[last];
    setErr(null);
    setExtra([]);
    setA(nextA);
    setLog((l) => ({ key: l.key + 1, from: script(d, nextA, []).messages.length }));
  };

  const sendText = () => {
    const v = text.trim();
    if (step === "name") {
      const name = v.replace(/^(hi[,!]?\s+)?(i'?m|i am|my name is|it'?s|call me)\s+/i, "").trim();
      if (name.length < 2) return setErr("Please tell me your name.");
      const ok = PersonName.safeParse(name);
      if (!ok.success) return setErr("Please type just your name — letters, spaces, hyphens and apostrophes.");
      if (!/\S\s+\S/.test(name)) return setErr("Could you add your surname too?");
      answer({ name });
    } else if (step === "dob") {
      const dob = parseDateOfBirth(v);
      if (!dob) return setErr("Please type your date of birth as day, month and year — for example 14/03/1992.");
      answer({ dob });
    } else if (step === "gender") {
      // Typed rather than tapped: their own words.
      if (v.length < 2) return setErr("Tap one of the options, or type how you'd describe it.");
      answer({ gender: { value: "self_described", description: v.slice(0, 40) } });
    } else if (step === "phone") {
      const phone = normalisePhone(v);
      if (!phone) return setErr("That doesn't look like a mobile number. Try e.g. 07700 900123 or +44 7700 900123.");
      answer({ phone });
    } else if (step === "email") {
      if (/^(no|skip|nope|no thanks)$/i.test(v)) return answer({ email: null });
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return setErr("That doesn't look like an email address — or tap Skip.");
      answer({ email: v.toLowerCase() });
    } else if (step === "limits") {
      if (/^(no|nope|none|nothing|no thanks|that'?s it|all good|fine)\.?$/i.test(v)) return answer({ limits: null });
      const limits = parseTimeLimits(v);
      if (!limits) return setErr("I didn't catch a time there — try something like “not before 8:30am”, or tap “No, that's it”.");
      const narrowed = applyTimeLimits(a.prefs ?? [], limits, d.sessionLengthMinutes, "05:00", "22:00");
      if (!narrowed.length) return setErr(`That leaves no time long enough for a ${d.sessionLengthMinutes}-minute session — try a different limit.`);
      answer({ limits: { text: v, prefs: narrowed } });
    } else if (step === "sessions") {
      const n = Number(v.match(/\d/)?.[0]);
      if (!(n >= 1 && n <= 7)) return setErr("Pick a number from 1 to 7.");
      answer({ sessions: n });
    }
  };

  const join = async () => {
    if (!boxes.acceptPrivacy) return setErr("Please tick the first box to continue.");
    const parsed = ClientSelfSignupInput.safeParse({
      name: a.name,
      phone: a.phone,
      email: a.email ?? undefined,
      sessionsPerWeek: a.sessions,
      preferences: a.limits?.prefs ?? a.prefs,
      acceptPrivacy: boxes.acceptPrivacy,
      confirmAdult: !!a.dob && ageOn(a.dob) >= 18,
      dateOfBirth: a.dob,
      gender: a.gender?.value,
      genderDescription: a.gender?.description,
      whatsappOptIn: boxes.whatsappOptIn,
      inviteToken: token,
    });
    if (!parsed.success) return setErr(parsed.error.issues[0]?.message ?? "Something's not quite right — go back and check.");
    answer({ consent: { whatsappOptIn: boxes.whatsappOptIn } });
    setBusy(true);
    try {
      const r = await api.post<{ portalUrl: string }>("/api/clients/invite", parsed.data);
      setPortal(r.portalUrl);
      setExtra([
        { role: "bot", text: `You're in. ${tf} can now see when you can train.` },
        { role: "bot", text: boxes.whatsappOptIn ? "Each week I'll WhatsApp you to check your usual times still work — just reply." : `You've said no to WhatsApp messages, so ${tf} will be in touch about sessions.` },
        { role: "bot", text: `You also have your own private page for changing your times. Open it and I'll help you bookmark it.${boxes.whatsappOptIn ? " I'll send you the link on WhatsApp too." : ""}` },
      ]);
    } catch (ex) {
      const msg = (ex as Error).message;
      const dup = /already registered/i.test(msg);
      // Back to the question that needs fixing, with RackUp explaining why.
      setA((x) => ({ ...x, consent: undefined, ...(dup ? { phone: undefined, email: undefined, sessions: undefined, prefs: undefined } : {}) }));
      setLog((l) => ({ key: l.key + 1, from: 10_000 }));
      setErr(dup ? `That number is already signed up with ${tf}. Check your WhatsApp for your private link, or use a different number.` : msg);
    } finally {
      setBusy(false);
    }
  };

  const placeholder = { name: "First and last name", dob: "e.g. 14/03/1992", gender: "Or describe it in your own words", phone: "e.g. 07700 900123", email: "you@example.com", sessions: "Or type a number", limits: "e.g. not before 8:30am" }[step as string];

  return (
    <Page s={s}>
      <Card className={s.trainer} aria-label="Your trainer">
        {d.photoUrl && <Avatar name={d.trainerName} image={{ src: d.photoUrl, alt: `Photo of ${d.trainerName}` }} size={64} />}
        <div style={{ minWidth: 0 }}>
          <Text weight="semibold" size={400} block>{d.trainerName}</Text>
          <div className={s.meta}>
            <Text size={200}>Personal trainer</Text>
            {gym && (
              <Text size={200} className={s.metaItem}>
                <Location16Regular /> {gym.name}
                {gym.city && !gym.name.toLowerCase().includes(gym.city.toLowerCase()) ? `, ${gym.city}` : ""}
              </Text>
            )}
            <Text size={200} className={s.metaItem}>
              <Timer16Regular /> {d.sessionLengthMinutes}-min sessions
            </Text>
          </div>
        </div>
      </Card>

      <Card className={s.chat}>
        <ChatLog key={log.key} messages={sc.messages} thinking={busy} animateFrom={log.from} onSettled={onSettled} />

        {settled && !busy && step && (
          <div className={s.composer}>
            {step === "intro" && (
              <div className={s.chips}>
                <Button shape="circular" appearance="primary" onClick={() => answer({ intro: "yes" })}>Yes, that's {tf}</Button>
                <Button shape="circular" onClick={() => answer({ intro: "no" })}>No, that's not them</Button>
              </div>
            )}
            {step === "gender" && (
              <div className={s.chips}>
                {(["woman", "man", "non_binary", "prefer_not_to_say"] as const).map((g) => (
                  <Button key={g} shape="circular" onClick={() => answer({ gender: { value: g } })}>{GENDER_LABEL[g]}</Button>
                ))}
                <Button shape="circular" appearance="subtle" onClick={() => inputRef.current?.focus()}>In my own words…</Button>
              </div>
            )}
            {step === "underage" && (
              <div className={s.chips}>
                <Button shape="circular" onClick={undo}>I typed my date of birth wrong</Button>
              </div>
            )}
            {step === "declined" && (
              <div className={s.chips}>
                <Button shape="circular" onClick={undo}>Actually, it is {tf}</Button>
              </div>
            )}
            {step === "email" && (
              <div className={s.chips}>
                <Button shape="circular" onClick={() => answer({ email: null })}>Skip</Button>
              </div>
            )}
            {step === "limits" && (
              <div className={s.chips}>
                <Button shape="circular" onClick={() => answer({ limits: null })}>No, that's it</Button>
              </div>
            )}
            {step === "sessions" && (
              <div className={s.chips}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Button key={n} shape="circular" onClick={() => answer({ sessions: n })}>{n}</Button>
                ))}
              </div>
            )}
            {(step === "name" || step === "dob" || step === "gender" || step === "phone" || step === "email" || step === "sessions" || step === "limits") && (
              <form className={s.inputRow} onSubmit={(e) => { e.preventDefault(); sendText(); }}>
                <Input
                  ref={inputRef}
                  style={{ flex: 1 }}
                  value={text}
                  onChange={(_, v) => setText(v.value)}
                  placeholder={placeholder}
                  aria-label="Your answer"
                  autoFocus
                  maxLength={step === "limits" ? 200 : 100}
                  type={step === "phone" ? "tel" : step === "email" ? "email" : "text"}
                  inputMode={step === "phone" ? "tel" : step === "email" ? "email" : step === "sessions" ? "numeric" : "text"}
                  autoComplete={step === "name" ? "name" : step === "phone" ? "tel" : step === "email" ? "email" : step === "dob" ? "bday" : "off"}
                />
                <Button type="submit" appearance="primary" icon={<Send20Filled />} disabled={!text.trim()} aria-label="Send" />
              </form>
            )}
            {step === "prefs" && (
              <>
                <div className={s.days}>
                  <DayChips value={prefs} onChange={setPrefs} availability={d.availability} />
                </div>
                <Button appearance="primary" disabled={prefs.length === 0} onClick={() => answer({ prefs })}>
                  {prefs.length === 0 ? "Pick at least one day" : "Done"}
                </Button>
              </>
            )}
            {step === "consent" && (
              <>
                <Checkbox checked={boxes.acceptPrivacy} onChange={(_, v) => { setErr(null); setBoxes({ ...boxes, acceptPrivacy: !!v.checked }); }} label={<>I've read the <Link to="/privacy" target="_blank">privacy notice</Link> and agree to {tf} using RackUp to arrange my sessions.</>} />
                <Checkbox checked={boxes.whatsappOptIn} onChange={(_, v) => { setErr(null); setBoxes({ ...boxes, whatsappOptIn: !!v.checked }); }} label="Send me WhatsApp messages about my sessions (reply STOP any time)." />
                <Button appearance="primary" size="large" onClick={() => void join()}>Sign me up</Button>
              </>
            )}
            {err && <Text size={200} className={s.error} role="alert">{err}</Text>}
            {step !== "intro" && step !== "declined" && step !== "underage" && (
              <Button size="small" appearance="subtle" icon={<ArrowUndo16Regular />} onClick={undo} style={{ alignSelf: "flex-start" }}>
                Change my last answer
              </Button>
            )}
          </div>
        )}

        {settled && portal && (
          <div className={s.composer}>
            <Button as="a" href={portal} appearance="primary" icon={<Open16Regular />}>Open my page</Button>
            <Text size={200} className={s.hint} style={{ wordBreak: "break-all" }}>{portal}</Text>
          </div>
        )}
      </Card>

      <Text size={200} className={s.secure}>
        <LockClosed16Regular /> Encrypted and only used to arrange your sessions · <Link to="/privacy">Privacy</Link>
      </Text>
    </Page>
  );
}

function Page({ s, children }: { s: ReturnType<typeof useStyles>; children: React.ReactNode }) {
  return (
    <div className={s.page}>
      <div className={s.col}>
        <Wordmark height={32} />
        {children}
      </div>
    </div>
  );
}
